import { format, parseISO } from "date-fns";

/** Format a time string (HH:MM:SS or HH:MM) to HH:MM */
// `formatTime` used to live here: a plain "HH:MM" cut that ignored the
// 12h/24h setting. It was the reason that setting did nothing on a dozen
// screens — it sat in the shared package looking like the obvious choice, and
// every screen that reached for it silently opted out of the user's
// preference. Removed rather than renamed: there is no case where a
// user-facing time should ignore the setting *on a screen*, and
// `formatTimeWithSetting(t, "24h")` says it out loud for the places that
// genuinely must.
//
// Two of those places exist today, both deliberate: the iOS Live Activity
// payload (`liveTracker.ts`, mirrored in `_shared/liveActivityState.ts`) is
// built server-side without the crew member's setting in hand, and the CSV
// export in Analytics is read by spreadsheets rather than people. Both now
// call formatTimeWithSetting with an explicit "24h" so the choice is visible.

/** Format a date range for display */
export const formatDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  dateFormat = "MMMM d, yyyy",
): string => {
  if (!startDate) return "";
  const start = format(parseISO(startDate), dateFormat);
  if (!endDate || endDate === startDate) return start;
  const end = format(parseISO(endDate), dateFormat);
  return `${start} - ${end}`;
};

/** Format a date for display */
export const formatDate = (
  date: string | null | undefined,
  dateFormat = "MMMM d, yyyy",
): string => {
  if (!date) return "";
  return format(parseISO(date), dateFormat);
};

/**
 * Short, weekday-prefixed format used in the per-day shift breakdown
 * on the briefing tab — keeps "Your roles" / call-time lines compact
 * while still letting a crew member glance "Wed 6 May" without
 * decoding the iso string.
 */
export const formatShiftDay = (date: string | null | undefined): string => {
  if (!date) return "";
  return format(parseISO(date), "EEE d MMM");
};

/**
 * Canonical label for a festival day. The "Day N" prefix (1-based) is
 * ALWAYS present; the suffix is the producer's custom name when set
 * (e.g. "Day 1 - Harmony of Hardcore"). When no name is given the label
 * is simply "Day 1" / "Day 2" — no date suffix. One source of truth so
 * the wizard, review/publish, portal briefing and the crew app render
 * days identically.
 */
export const formatDayLabel = (input: { index: number; customName?: string | null }): string => {
  const prefix = `Day ${input.index + 1}`;
  const custom = (input.customName ?? "").trim();
  return custom ? `${prefix} - ${custom}` : prefix;
};

/**
 * Resolve the custom name for a single day from the `briefings.day_names`
 * map (keyed by event_date "yyyy-mm-dd"). Returns "" when none is set.
 * Tolerates the loosely-typed Json the column round-trips as.
 */
export const dayNameFor = (dayNames: unknown, eventDate: string | null | undefined): string => {
  if (!eventDate || !dayNames || typeof dayNames !== "object" || Array.isArray(dayNames)) {
    return "";
  }
  const value = (dayNames as Record<string, unknown>)[eventDate];
  return typeof value === "string" ? value.trim() : "";
};

/**
 * Format a time string honoring the user's 12h/24h preference
 * (crew_settings.time_format / producer schedule_time_format). One source
 * of truth for the whole app — the schedule creator, read-only timeline,
 * lineup and crew screens all render times identically.
 *
 * Tolerant on purpose so it is a safe drop-in for the many ad-hoc copies it
 * replaces: an empty/missing time yields "" and an unknown/undefined format
 * falls back to 24h.
 */
export function formatTimeWithSetting(
  time: string | null | undefined,
  timeFormat: "24h" | "12h" | string | null | undefined = "24h",
): string {
  if (!time) return "";
  const hhmm = time.substring(0, 5);
  // 24h is the default and the common case: no parse needed.
  if (timeFormat !== "12h") return hhmm;
  const [hours, minutes] = hhmm.split(":");
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
}

/**
 * Compare two HH:MM time strings for sorting, treating times before 06:00
 * as "next day" so they sort after late-night times (e.g. 23:00).
 */
export function compareSetTimes(a: string, b: string): number {
  if (!a || !b) return 0;
  const toMinutes = (t: string) => {
    const [h, m] = t.substring(0, 5).split(":").map(Number);
    return h < 6 ? h * 60 + m + 1440 : h * 60 + m;
  };
  return toMinutes(a) - toMinutes(b);
}
